import { useEffect, useState } from 'react';
import styled from 'styled-components';
import QuantitySelector from 'components/ui-components/QuantitySelector';
import { useDebouncedCallback } from 'use-debounce';

import usePricingStore from 'stores/pricing_store/usePricingStore';
import useProductShowStore from 'stores/product_show_store/useProductShowStore';

import {
  isMinOrderQuantityValid,
  isMaxOrderQuantityValid,
} from 'lib/addToBasketHelpers';

import { useGET } from 'hooks/useRequest';
import { PlusIcon, MinusIcon } from 'assets/icons';

import { grey30, ctaGreen } from 'constants/variables';

export const QuantitySelectorError = ({ quantitySelectorError }) => (
  <p
    id="cef-quantity-selector__error-text"
    className="cef-quantity-selector__error-text no-margin tc-cef-red fs-14">
    {quantitySelectorError}
  </p>
);

/**
 * Quantity selector which fetches prices on change
 * @param {Object} props
 * @param {string} stepId
 * @param {number} minOrderValue - product minimum order value
 * @param {number} maxOrderValue - product maximum order value
 * @param {string} fetchPriceUrl - the url to fetch prices
 * @param {boolean} type - to determine where the button is placed e.g. - sticky basket
 */
const CEFQuantitySelector = ({
  stepId,
  minOrderValue,
  maxOrderValue,
  fetchPriceUrl,
  type,
}) => {
  const [priceRequestOptions, setPriceRequestOptions] = useState(null);

  const {
    quantitySelectorInput,
    quantitySelectorError,
    quantitySelectorActions,
    setProductStock,
    setProductBasketQuantity,
    setShowBackOrderMessage,
  } = useProductShowStore();

  const { setPrice } = usePricingStore();

  const { data: priceResponse } = useGET(
    priceRequestOptions ? fetchPriceUrl : null,
    priceRequestOptions,
  );

  /**
   * fetch price with new quantity value
   * updating global quantity selector value
   * @param {number} qty - the new product quantity
   */
  const updatePrice = qty => {
    const { setQuantitySelectorInput } = quantitySelectorActions;
    const options = {
      params: {
        id: stepId,
        quantity: qty,
      },
    };

    setPriceRequestOptions(options);
    setQuantitySelectorInput(qty);
  };

  /**
   * increment handler - checking for max quantity
   * clearing errors on success
   */
  const onIncrement = useDebouncedCallback(qty => {
    const { setError, clearQuantitySelectorError } = quantitySelectorActions;
    if (isMaxOrderQuantityValid(qty, maxOrderValue)) {
      setError(
        +quantitySelectorInput,
        `More than maximum order of ${maxOrderValue}, quantity updated.`,
      );
    } else {
      updatePrice(qty);
      clearQuantitySelectorError();
    }
  }, 250);

  /**
   * decrement handler
   */
  const onDecrement = useDebouncedCallback(qty => {
    const { clearQuantitySelectorError } = quantitySelectorActions;

    // if value has not changed - do nothing
    if (qty === quantitySelectorInput) return false;

    updatePrice(qty);
    clearQuantitySelectorError();

    return true;
  }, 250);

  /**
   * focus out event for the quantity box
   * checking for minimum/maximum order quantity for quick user feedback
   * fetching the new price with the new quantity value
   */
  const onBlur = e => {
    const { value: qty } = e.target;
    const { setError } = quantitySelectorActions;

    if (isMinOrderQuantityValid(qty, minOrderValue)) {
      setError(
        +minOrderValue,
        `Quantity must be in multiples of ${minOrderValue}, quantity updated.`,
      );

      updatePrice(+minOrderValue);
    } else if (isMaxOrderQuantityValid(qty, maxOrderValue)) {
      setError(
        +minOrderValue,
        `More than maximum order of ${maxOrderValue}, quantity updated.`,
      );
      updatePrice(+minOrderValue);
    } else {
      setError(+qty, '');
      updatePrice(+qty);
    }
  };

  /**
   * onChange handler - just setting the value of the quantity box
   */
  const onChange = e => {
    const { value: qty } = e.target;
    const { setQuantitySelectorInput } = quantitySelectorActions;
    setQuantitySelectorInput(+qty);
  };

  useEffect(() => {
    setPriceRequestOptions(null); // unset price request to avoid unnecessary requests
    if (priceResponse) {
      const {
        product_prices: prices,
        stock_level: stockLevel,
        total_basket_and_requested_quantity: basketQuantity,
        errors,
      } = priceResponse;

      // Set our price in global store
      setPrice(prices);
      setProductStock(stockLevel);
      setProductBasketQuantity(basketQuantity);

      if (errors && Object.keys(errors)[0] === 'insufficient_stock_level') {
        setShowBackOrderMessage(true);
      } else {
        setShowBackOrderMessage(false);
      }
    }
  }, [priceResponse]);

  // Set quantity selector value in global state
  useEffect(() => {
    const { setQuantitySelectorInput } = quantitySelectorActions;
    setQuantitySelectorInput(minOrderValue || 1);
  }, []);

  return (
    <div className="fl fl-col">
      <StyledQuantitySelector
        testIdPrefix={type}
        className={`cef-quantity-selector ${
          quantitySelectorError ? ' cef-quantity-selector__error' : ''
        }`}
        disabled={priceRequestOptions}
        value={quantitySelectorInput || ''}
        incrementValue={minOrderValue}
        label="quantity selector"
        aria-describedby="cef-quantity-selector__error-text" // need to implement in the ui-components library as well
        handleIncrement={onIncrement}
        handleDecrement={onDecrement}
        handleBlur={onBlur}
        handleChange={onChange}
        incrementIcon={<PlusIcon fillColor={ctaGreen} />}
        decrementIcon={
          // TODO: prettier conflict is playing up with the format
          // eslint-disable-next-line react/jsx-wrap-multilines
          <MinusIcon
            fillColor={
              quantitySelectorInput === minOrderValue ? grey30 : ctaGreen
            }
          />
        }
      />
    </div>
  );
};

const StyledQuantitySelector = styled(QuantitySelector)`
  height: 50px;
  flex-shrink: 0;
`;

export default CEFQuantitySelector;
